import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, Target, Flame, Lightbulb } from 'lucide-react';
import { TrainingStats } from '@/hooks/useTrainingStats';
import { useNavigate } from 'react-router-dom';

interface TrainingInsightsProps {
  stats: TrainingStats;
  improvementTrend: { trend: number; isImproving: boolean; recentRate: number; previousRate: number } | null;
  weakestAreas: Array<{ name: string; rate: number; attempts: number }>;
  strongestAreas: Array<{ name: string; rate: number; attempts: number }>;
}

export function TrainingInsights({ stats, improvementTrend, weakestAreas, strongestAreas }: TrainingInsightsProps) {
  const navigate = useNavigate();

  const insights = [];

  // Strongest area
  if (strongestAreas.length > 0) {
    const strongest = strongestAreas[0];
    insights.push({
      icon: Target,
      title: 'Your Strongest Area',
      description: `You excel at ${strongest.name} positions with ${strongest.rate}% success rate!`,
      color: 'text-teen-green',
      bgColor: 'bg-teen-green-bright'
    });
  }

  // Weakest area
  if (weakestAreas.length > 0) {
    const weakest = weakestAreas[0];
    insights.push({
      icon: Target,
      title: 'Area for Improvement',
      description: `${weakest.name} positions need work (${weakest.rate}% success rate). Practice more to improve!`,
      color: 'text-teen-orange',
      bgColor: 'bg-teen-orange-bright'
    });
  }

  // Improvement trend
  if (improvementTrend) {
    if (improvementTrend.isImproving) {
      insights.push({
        icon: TrendingUp,
        title: "You're Improving!",
        description: `Your recent success rate (${improvementTrend.recentRate}%) is up from ${improvementTrend.previousRate}%. Keep it up!`,
        color: 'text-teen-green',
        bgColor: 'bg-teen-green-bright'
      });
    } else {
      insights.push({
        icon: TrendingDown,
        title: 'Keep Pushing',
        description: `Your recent success rate is ${improvementTrend.recentRate}%. Stay consistent with your training!`,
        color: 'text-teen-blue',
        bgColor: 'bg-teen-blue-bright'
      });
    }
  }

  // Streak insights
  if (stats.currentStreak >= 7) {
    insights.push({
      icon: Flame,
      title: 'Amazing Streak!',
      description: `You're on a ${stats.currentStreak}-day streak! Your dedication is paying off!`,
      color: 'text-teen-orange',
      bgColor: 'bg-teen-orange-bright'
    });
  } else if (stats.currentStreak > 0) {
    const daysToSevenDay = 7 - stats.currentStreak;
    insights.push({
      icon: Flame,
      title: 'Streak Alert',
      description: `You're ${daysToSevenDay} day${daysToSevenDay !== 1 ? 's' : ''} away from a 7-day streak! Keep going!`,
      color: 'text-teen-gold',
      bgColor: 'bg-teen-gold-bright'
    });
  }

  // Mastery progress
  const masteryPercentage = stats.totalPositions > 0 
    ? Math.round((stats.totalMastered / stats.totalPositions) * 100)
    : 0;
  
  if (masteryPercentage >= 50) {
    insights.push({
      icon: Lightbulb,
      title: 'Great Progress!',
      description: `You've mastered ${masteryPercentage}% of your training positions. You're becoming a stronger player!`,
      color: 'text-teen-purple',
      bgColor: 'bg-teen-purple-bright'
    });
  }

  if (insights.length === 0) {
    insights.push({
      icon: Lightbulb,
      title: 'Start Your Journey',
      description: 'Complete more training sessions to receive personalized insights!',
      color: 'text-teen-blue',
      bgColor: 'bg-teen-blue-bright'
    });
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4">Insights & Recommendations</h3>
      <div className="space-y-4">
        {insights.map((insight, idx) => {
          const Icon = insight.icon;
          return (
            <div key={idx} className={`p-4 rounded-lg ${insight.bgColor}`}>
              <div className="flex items-start gap-3">
                <Icon className={`h-5 w-5 ${insight.color} mt-0.5 flex-shrink-0`} />
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">{insight.title}</h4>
                  <p className="text-sm text-muted-foreground">{insight.description}</p>
                </div>
              </div>
            </div>
          );
        })}
        
        <Button 
          onClick={() => navigate('/training')}
          className="w-full mt-4"
        >
          Continue Training
        </Button>
      </div>
    </Card>
  );
}
